import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { SpaceOut } from "./api/types.gen.ts";
import { t } from "./i18n/index.ts";
import { useAnchor } from "./lib/asked.ts";
import { triageTarget } from "./lib/attach.ts";
import { useBookmarksLoader } from "./lib/bookmarks.ts";
import { useCommentCountsLoader } from "./lib/commentCounts.ts";
import { useSpace } from "./lib/data.ts";
import { useEntitiesLoader } from "./lib/entities.ts";
import { type DropTarget, useDropTarget } from "./lib/fileDrop.ts";
import { useKeepInView } from "./lib/keepInView.ts";
import { useLive } from "./lib/live.ts";
import { useMedia } from "./lib/media.ts";
import { fragment, provides } from "./lib/native.ts";
import { navHidden, setNavHidden } from "./lib/navHidden.ts";
import { peeked, splitSide } from "./lib/peek.ts";
import { usePeopleLoader } from "./lib/people.ts";
import { bump } from "./lib/resource.ts";
import { canEdit } from "./lib/roles.ts";
import { herePath, location, spacePath } from "./lib/router.ts";
import { homeSpace, rememberSpace, showsPrivate } from "./lib/state.ts";
import { useId } from "./lib/uid.ts";
import { Omnibar, OmnibarButton } from "./omnibar/Omnibar.tsx";
import { authPath } from "./pages/auth/flow.ts";
import { ItemPeek } from "./pages/ItemPeek.tsx";
import { OthersPrivateBanner } from "./pages/items/othersPrivate.tsx";
import { PlanBanner } from "./pages/settings/planBanner.tsx";
import { findRoute } from "./routes.ts";
import { DrawerHead, PhoneBar } from "./shell/Drawer.tsx";
import { ImportChip } from "./shell/ImportChip.tsx";
import { AccountNav, Nav } from "./shell/Nav.tsx";
import { SpaceMenu } from "./shell/SpaceMenu.tsx";
import { SplitBar, TopSentinel } from "./shell/TopBar.tsx";
import { TrialChip } from "./shell/TrialChip.tsx";
import { SAFE_BOTTOM, useDrawer } from "./shell/useDrawer.ts";
import { IconButton, Link } from "./ui/Button.tsx";
import { Crash } from "./ui/Crash.tsx";
import { CloseIcon, HamburgerIcon, HideNavIcon, ShowNavIcon } from "./ui/icons.tsx";
import { Logo } from "./ui/Logo.tsx";
import { Async } from "./ui/misc.tsx";
import { buttonClass, iconButtonClass, SPLIT } from "./ui/styles.ts";

/** The wordmark, as a sidebar heads with it. */
const Wordmark = () => <Logo class="text-3xl text-graphite" />;

/** The button that shows and hides the sidebar's menu (on a phone, or beside a pinned look). */
function MenuToggle(p: { open: boolean; controls: string; onToggle: () => void; class: string }) {
  return (
    <IconButton
      size="md"
      class={p.class}
      label={t("nav.menu")}
      aria-expanded={p.open}
      aria-controls={p.controls}
      onClick={p.onToggle}
    >
      {p.open ? <CloseIcon size={20} /> : <HamburgerIcon size={20} />}
    </IconButton>
  );
}

/** Keeps the organization's members and the space's entities loaded, for pictures, names and icons anywhere. */
function PeopleLoader({ space }: { space: SpaceOut }) {
  usePeopleLoader(space);
  useEntitiesLoader(space);
  return null;
}

interface FrameProps {
  /** The space the sidebar navigates (null: none yet). */
  space: SpaceOut | null;
  /** The space section shown (highlighted in its navigation), if any. */
  section: string | null;
  /** One of your account's pages is shown. */
  account: boolean;
  children: ComponentChildren;
}

/**
 * Half a window this wide holds the sidebar and a page in two columns (18rem, 48rem and the page's margins): beside a
 * pinned look, the sidebar stays.
 */
const ROOMY = "(min-width: 140rem)";
/** Wide enough for the sidebar beside the page (Tailwind's md). */
const WIDE = "(min-width: 48rem)";

/** How long the sidebar takes to fold (its width's transition). */
const FOLD_MS = 300;

/**
 * Whether the folded sidebar's content is gone (`display: none`), once it's done folding (it's clipped meanwhile, as
 * the sidebar narrows): out of sight, its sticky box still lay over the page's left edge, and Safari, taking it for
 * something over the page, drew the document's zoomed pages (a transform) from a layer of their own at 100%, scaled
 * up: blurred. Back at once as it opens.
 */
function useGone(folded: boolean): boolean {
  const [gone, setGone] = useState(folded);
  useEffect(() => {
    if (!folded) {
      setGone(false);
      return;
    }
    const timer = setTimeout(() => setGone(true), FOLD_MS);
    return () => clearTimeout(timer);
  }, [folded]);
  return gone && folded;
}

/**
 * Every signed-in page: the sidebar (space switcher, the space's sections, your account) and the page. Beside a pinned
 * look (half the window), the sidebar folds away, sliding as the page makes room; a bar atop the page, as a phone's,
 * brings it back with its menu button.
 */
function Frame({ space, section, account, children }: FrameProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navId = useId();
  const mainId = useId();
  const drawer = useDrawer({ toggleId: useId(), closeId: useId(), panelId: navId });
  const page = useRef<HTMLElement>(null);
  useKeepInView(page);
  useAnchor();
  // The sidebar folds beside a pinned look, unless the window's wide enough for both.
  const roomy = useMedia(ROOMY);
  const folds = splitSide() !== null && !roomy;
  // Or hidden by its button (otherwise; a pinned look's bar brings it back then): out of the way of the page. On a wide
  // screen only: on a phone it's the menu (hidden there, it couldn't be opened).
  const wide = useMedia(WIDE);
  const hidden = navHidden.value && !folds && wide;
  const folded = (folds && !menuOpen) || hidden;
  const gone = useGone(folded);
  const menuButton = (cls: string) => (
    <MenuToggle class={cls} open={menuOpen} controls={navId} onToggle={() => setMenuOpen(!menuOpen)} />
  );
  // Going somewhere from the sidebar: on a phone its menu closes (the page there from its top), a folded one folds.
  const close = () => {
    setMenuOpen(false);
    drawer.close(true);
  };
  // The app's sidebar, alone (its fragment, in a web view of its own: the app shows the pages): what the sidebar has,
  // the page's own not drawn (nor loaded), the app's glass behind it.
  if (fragment() === "sidebar") {
    return (
      <nav aria-label={t("nav.menu")} class="flex min-h-dvh flex-col px-3 pt-1 pb-3">
        <div class="flex items-center px-3 pt-1 pb-3">
          <Wordmark />
        </div>
        {space ? <SpaceMenu space={space} onNavigate={close} /> : null}
        <div class="mt-3">
          {space ? <TrialChip space={space} onNavigate={close} /> : null}
          {space ? <Nav space={space} section={section ?? ""} onNavigate={close} /> : null}
          <AccountNav current={account} onNavigate={close} />
        </div>
        {space ? <PeopleLoader space={space} /> : null}
      </nav>
    );
  }
  return (
    // Pinned, a look takes half the window: the room left for the app is this one's padding. On a phone, a look is a
    // sheet over the bottom half: room under the page to scroll its end up above it.
    <div
      class={`transition-[padding] duration-300 ease-out motion-reduce:transition-none ${SPLIT[splitSide() ?? "none"]} ${peeked() === null ? "" : "max-md:pb-[50dvh]"}`}
    >
      <TopSentinel />
      <div class="min-h-dvh md:flex">
        <a
          href={`#${mainId}`}
          inert={drawer.open ? true : undefined}
          class="sr-only focus:not-sr-only focus:absolute focus:m-2 focus:rounded focus:bg-white focus:p-2"
        >
          {t("nav.skip")}
        </a>
        {/* The aside stretches to the page's full height (background); its content sticks to the viewport. Folding,
            it narrows to nothing, its content (as wide as ever) clipped as it goes: clipped, not hidden, so the aside
            doesn't become a scroller of its own, and its content still sticks to the viewport. On a phone it's a bar
            atop the screen as the page scrolls (menu, home, search; under a quick look's sheet; clear at the page's
            top), and its content a menu over the whole screen (over the sheet too). */}
        {/* In an app with a sidebar of its own (its native one), none here. */}
        {provides("sidebar") ? null : (
          <aside
            inert={folded ? true : undefined}
            class={`border-zinc-200 max-md:sticky max-md:top-0 ${drawer.open ? "max-md:z-40" : "max-md:z-20"} md:shrink-0 md:overflow-x-clip md:bg-zinc-100 md:transition-[width] md:duration-300 md:ease-out motion-reduce:transition-none ${folded ? "md:w-0" : "md:w-72 md:border-r"}`}
          >
            <PhoneBar space={space} drawer={drawer} />
            <div
              id={navId}
              {...drawer.dialog}
              class={`p-3 md:sticky md:top-0 md:max-h-dvh md:w-72 md:overflow-y-auto ${gone ? "md:hidden" : ""} ${drawer.panelClass}`}
            >
              <DrawerHead space={space} drawer={drawer} />
              {/* The wordmark heads the sidebar on wide screens. */}
              <div class="hidden items-center px-3 pt-1 pb-3 md:flex">
                <div class="flex-1">
                  <Wordmark />
                </div>
                {folds ? null : (
                  <button
                    type="button"
                    class={iconButtonClass("sm")}
                    aria-pressed={false}
                    aria-label={t("nav.hide")}
                    title={t("nav.hide")}
                    onClick={() => setNavHidden(true)}
                  >
                    <HideNavIcon />
                  </button>
                )}
              </div>
              {/* On a phone, what's under the menu's head scrolls on its own. */}
              <div
                class={`max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto max-md:overscroll-contain ${SAFE_BOTTOM}`}
              >
                {space ? <SpaceMenu space={space} onNavigate={close} /> : null}
                {/* Search: a field under the space on wider screens (on a phone, an icon in the bar). */}
                <OmnibarButton class="mt-2 hidden md:flex" />
                <div class="mt-3">
                  {space ? <TrialChip space={space} onNavigate={close} /> : null}
                  {space ? <Nav space={space} section={section ?? ""} onNavigate={close} /> : null}
                  <AccountNav current={account} onNavigate={close} />
                </div>
              </div>
            </div>
          </aside>
        )}
        <main
          ref={page}
          id={mainId}
          inert={drawer.open ? true : undefined}
          data-nav-folded={folded ? "" : undefined}
          // In an app drawing titles, its bar's title has the room over the page: less of the page's own.
          class={`@container/main min-w-0 flex-1 p-4 md:p-8 ${provides("title") ? "pt-2 md:pt-2" : ""}`}
        >
          {/* Beside a pinned look: a bar atop the page, as on a phone (the sidebar behind its menu button). */}
          {folds ? <SplitBar space={space} menu={menuButton("")} menuOpen={menuOpen} /> : null}
          {/* Hidden by its button: a button in the page's corner brings it back (no bar of its own). Its wrapper hides
              it on a phone (the button's display would win over `hidden` on it). */}
          {hidden && !provides("sidebar") ? (
            <span class="fixed top-1.5 left-1.5 z-30 hidden md:inline-flex">
              <button
                type="button"
                class={iconButtonClass("sm")}
                aria-pressed={true}
                aria-label={t("nav.show")}
                title={t("nav.show")}
                onClick={() => setNavHidden(false)}
              >
                <ShowNavIcon />
              </button>
            </span>
          ) : null}
          {children}
        </main>
      </div>
      <Omnibar space={space} />
      <ImportChip />
    </div>
  );
}

function WithSpace({ spaceId, children }: { spaceId: string; children: ComponentChildren }) {
  const space = useSpace(spaceId);
  return (
    <Frame space={space.data ?? null} section={null} account={true}>
      {children}
    </Frame>
  );
}

/** Your account's pages, in the same frame: the sidebar keeps the space you last used. */
export function AccountShell({ children, wide = false }: { children: ComponentChildren; wide?: boolean }) {
  const spaceId = homeSpace();
  const page = wide ? children : <div class="max-w-2xl">{children}</div>;
  return spaceId ? (
    <WithSpace spaceId={spaceId}>{page}</WithSpace>
  ) : (
    <Frame space={null} section={null} account={true}>
      {page}
    </Frame>
  );
}

/**
 * The frame for someone not signed in (on a public item): no space, no account; what Revisa is, and ways in (creating
 * an account, signing in), back here after.
 */
export function GuestShell({ children }: { children: ComponentChildren }) {
  const back = herePath();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  return (
    <div class="min-h-dvh md:flex">
      <aside class="border-b border-zinc-200 bg-zinc-100 md:w-72 md:shrink-0 md:border-r md:border-b-0">
        <div class="p-3 md:sticky md:top-0 md:p-6">
          {/* On small screens one row, as the app's (menu, wordmark, a way in); the rest in the menu. */}
          <div class="flex items-center gap-1 md:block">
            <MenuToggle class="md:hidden" open={menuOpen} controls={menuId} onToggle={() => setMenuOpen(!menuOpen)} />
            <p class="min-w-0 flex-1 px-3 py-1.5 md:p-0">
              <Wordmark />
            </p>
            <Link href={authPath("/signin", {}, back)} class={`${buttonClass("secondary", "sm")} md:hidden`}>
              {t("login.signIn")}
            </Link>
          </div>
          <div id={menuId} class={`mt-3 flex-col gap-3 px-3 md:flex md:px-0 ${menuOpen ? "flex" : "hidden"}`}>
            <p class="text-sm text-zinc-600">{t("login.tagline")}</p>
            <p class="text-sm text-zinc-800">{t("public.join")}</p>
            <Link href={authPath("/signup", {}, back)} class={buttonClass("primary")}>
              {t("public.signUp")}
            </Link>
            <Link href={authPath("/signin", {}, back)} class={buttonClass()}>
              {t("login.signIn")}
            </Link>
          </div>
        </div>
      </aside>
      <main class="min-w-0 flex-1 p-4 md:p-8">{children}</main>
    </div>
  );
}

/** Where files dropped in the space go when the page doesn't say: your private triage area, if you can add any. */
const spaceDrop = (space: SpaceOut): DropTarget =>
  canEdit(space)
    ? triageTarget(space, { mine: true, level: 0, link: true })
    : { key: "", label: t("drop.readOnly"), busy: "", level: 0 };

/**
 * A space's pages, in the frame. Live: any event in the space refetches what's on screen (debounced); the stream opens
 * again when you start or stop seeing other members' private items.
 */
export function Shell({ spaceId }: { spaceId: string }) {
  const space = useSpace(spaceId);
  useLive(`/spaces/${spaceId}`, bump, showsPrivate({ id: spaceId }));
  useCommentCountsLoader(spaceId);
  useBookmarksLoader(spaceId);
  useEffect(() => rememberSpace(spaceId), [spaceId]);
  const rest = spacePath(location.value.pathname)?.rest ?? "triage";
  const route = findRoute(rest);
  // Files dropped on a page that has no say of its own go to your private triage area (a viewer is told they can't add any).
  useDropTarget(space.data ? spaceDrop(space.data) : null);
  const Page = route?.page;
  return (
    <>
      <Frame space={space.data ?? null} section={rest.split("/")[0] ?? ""} account={false}>
        <Async r={space} whole="page">
          {(o) => (
            <Crash key={rest}>
              <PeopleLoader space={o} />
              {route?.plan ? <PlanBanner space={o} /> : null}
              {route?.items ? <OthersPrivateBanner space={o} /> : null}
              {Page && route ? <Page space={o} params={route.params} /> : <p>{t("common.notFound")}</p>}
            </Crash>
          )}
        </Async>
      </Frame>
      {/* Over (or beside) the app, outside its frame: the frame lays itself out by its own width. */}
      {space.data ? <ItemPeek space={space.data} /> : null}
    </>
  );
}
